import React, { useEffect, useState } from "react";
import { Card, Col, Empty, Image, Row, Spin, theme } from "antd";
import {
  AuditOutlined,
  CalendarOutlined,
  CarOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  IdcardOutlined,
  LaptopOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  ToolOutlined,
  UserAddOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiService, TemplateTypeFilteredByGroups } from "../api";
import { PageHeader } from "../components/PageHeader";
import { space } from "../theme/space";

/**
 * The first screen after signing in: what can I raise?
 *
 * A tile per process, grouped by type — the shape people already know.
 *
 * **Why the tiles used to look empty.** Each one drew `t.icon`, and every
 * template in this deployment has that field blank, so the tile rendered a
 * name floating in whitespace where an icon should have been. The picture was
 * missing, not the design.
 *
 * So: a client's uploaded icon still wins whenever there is one — that is the
 * product feature, and an administrator who uploads a company mark should see
 * it. Where there is none, `iconFor` picks a built-in one from what the process
 * is *called*, so a fresh deployment looks finished on day one instead of
 * waiting for eight uploads.
 *
 * **Built-in rather than fetched from the web** for three practical reasons:
 * these deployments sit behind corporate proxies and are expected to work with
 * no outbound access; a remote image is a licence question nobody wants to
 * answer for a client install; and a mixed set of found artwork will not share
 * a stroke weight or an optical size, which is exactly what makes a grid of
 * icons look assembled rather than designed.
 */

/** Keyword → icon. Matched on what the process is called, not on its id, so a
 *  client's own "Разрешение на работы" or "Hot Work Permit" lands correctly
 *  without anybody registering it here. */
const ICON_RULES: Array<[RegExp, React.ComponentType<{ style?: React.CSSProperties }>]> = [
  // Order is the whole design here: a rule matches on the first hit, so the
  // more specific noun has to come first. "Work Safety Analysis" is an
  // analysis before it is a safety document — matched the other way round it
  // drew the same shield as the two permits beside it, and three tiles wearing
  // one icon is worse than three tiles wearing none.
  [/analysis|assessment|jsa|анализ|оценка|талдау/i, FileSearchOutlined],
  [/permit|наряд|рұқсат/i, SafetyCertificateOutlined],
  [/incident|accident|near.?miss|инцидент|происшеств/i, WarningOutlined],
  [/safety|hazard|risk|безопасн|қауіпс/i, SafetyOutlined],
  [/purchase|procure|requisition|supplier|закуп|снабж/i, ShoppingCartOutlined],
  [/expense|invoice|payment|budget|finance|расход|счёт|бюджет/i, DollarOutlined],
  [/access|visitor|contractor|badge|pass|доступ|подряд/i, IdcardOutlined],
  [/engineering|technical|design|инженер|техничес/i, ToolOutlined],
  [/maintenance|repair|work.?order|ремонт|обслуж/i, ToolOutlined],
  [/change|modification|moc|изменен|өзгеріс/i, SwapOutlined],
  [/leave|holiday|absence|vacation|отпуск|демалыс/i, CalendarOutlined],
  [/onboard|employee|staff|hr|персонал|сотрудник/i, UserAddOutlined],
  [/travel|trip|командиров/i, CarOutlined],
  [/service|helpdesk|support|it\b|заявка в ит/i, LaptopOutlined],
  [/audit|inspection|review|аудит|провер/i, AuditOutlined],
  [/approval|document|согласован|документ/i, FileDoneOutlined],
];

const iconFor = (name: string, shortName: string) => {
  const haystack = `${name} ${shortName}`;
  const rule = ICON_RULES.find(([pattern]) => pattern.test(haystack));
  return rule ? rule[1] : FileTextOutlined;
};

const HomePage = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [templateTypes, setTemplateTypes] = useState<
    TemplateTypeFilteredByGroups[]
  >([]);
  const [loading, setLoading] = useState(true);

  const getTemplateTypes = async () => {
    try {
      const res = await ApiService.apiMainTemplateTypesList("user");
      setTemplateTypes(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTemplateTypes();
  }, []);

  const groups = templateTypes.filter(
    (tt) => (tt.filtered_templates?.length ?? 0) > 0,
  );

  return (
    <div>
      <PageHeader title={t("New request")} />

      {loading ? (
        <div style={{ padding: `${space.band}px 0`, textAlign: "center" }}>
          <Spin />
        </div>
      ) : groups.length === 0 ? (
        <Empty
          style={{ marginTop: space.band }}
          description={t(
            "No processes are available to you yet. An administrator grants access per process.",
          )}
        />
      ) : (
        groups.map((tt) => (
          <div key={tt.id} style={{ marginBottom: space.band }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: token.colorText,
                marginBottom: space.snug,
              }}
            >
              {t(tt.name)}
            </div>
            {/* Responsive rather than a fixed six-per-row: `span={4}` put six
                tiles on a row at every width, so on a laptop the names wrapped
                to three lines inside a tile too narrow to read them. */}
            <Row gutter={[space.base, space.base]}>
              {tt.filtered_templates.map((template) => {
                const Icon = iconFor(template.name ?? "", template.short_name ?? "");
                return (
                  <Col
                    key={template.id}
                    xs={12}
                    sm={8}
                    md={6}
                    lg={6}
                    xl={4}
                    xxl={4}
                  >
                    <Link to={`requests/${template.short_name}/create`}>
                      <Card
                        hoverable
                        // One card, not a Card wrapping a Card.Grid: the grid
                        // drew a second border and its own shadow a pixel
                        // inside the first.
                        bodyStyle={{
                          padding: space.base,
                          textAlign: "center",
                          height: 118,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: space.tight,
                        }}
                      >
                        {template.icon ? (
                          <Image
                            src={template.icon}
                            width={40}
                            height={40}
                            preview={false}
                            style={{ objectFit: "contain" }}
                          />
                        ) : (
                          <Icon
                            style={{
                              fontSize: 30,
                              color: token.colorPrimary,
                              lineHeight: 1,
                            }}
                          />
                        )}
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: 1.35,
                            color: token.colorText,
                          }}
                        >
                          {t(template.name)}
                        </div>
                      </Card>
                    </Link>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))
      )}
    </div>
  );
};

export default HomePage;
